import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { Recipe } from '@/types/recipe';
import { Plus, Search, Settings, Star } from 'lucide-react';

interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  recipes: Recipe[];
  onAddRecipe: () => void;
  onManage: () => void;
  isAdmin: boolean;
}

const CommandPalette: React.FC<CommandPaletteProps> = ({
  open,
  onOpenChange,
  recipes,
  onAddRecipe,
  onManage,
  isAdmin
}) => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        onOpenChange(!open);
      }
    };

    document.addEventListener('keydown', down);
    return () => document.removeEventListener('keydown', down);
  }, [open, onOpenChange]);

  const filteredRecipes = recipes.filter(recipe =>
    recipe.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    recipe.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
    recipe.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput
        placeholder="Search recipes or actions..."
        value={searchQuery}
        onValueChange={setSearchQuery}
      />
      <CommandList>
        <CommandEmpty>No results found.</CommandEmpty>
        
        {/* Quick Actions */}
        <CommandGroup heading="Actions">
          {isAdmin && (
            <CommandItem onSelect={() => { onAddRecipe(); onOpenChange(false); }}>
              <Plus className="mr-2 h-4 w-4" />
              Add new recipe
            </CommandItem>
          )}
          {isAdmin && (
            <CommandItem onSelect={() => { onManage(); onOpenChange(false); }}>
              <Settings className="mr-2 h-4 w-4" />
              Manage recipes & users
            </CommandItem>
          )}
        </CommandGroup>
        
        {/* Recipes */}
        {filteredRecipes.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading="Recipes">
              {filteredRecipes.slice(0, 8).map((recipe) => (
                <CommandItem
                  key={recipe.id}
                  onSelect={() => {
                    navigate(`/recipe/${recipe.id}`);
                    onOpenChange(false);
                  }}
                >
                  <Search className="mr-2 h-4 w-4" />
                  <div className="flex items-center justify-between w-full">
                    <span>{recipe.title}</span>
                    {recipe.isFavorite && (
                      <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />
                    )}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}
      </CommandList>
    </CommandDialog>
  );
};

export default CommandPalette;